/*
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { jsGlobal } from './utilities/jsGlobal';
import { assert, Debug } from './utilities/Debug';
import { clamp } from './utilities/NumberUtilities';
import { RGBAToARGB, rgbaToCSSStyle } from './utilities/ColorUtilities';
import { base64EncodeBytes } from './utilities/StringUtilities';
import { ShumwayCom } from './external';
import { Shumway } from './utilities/Shumway';
//import {Promise} from 'es6-promise'
// Our polyfills for some DOM things make testing this slightly more onerous than it ought to be.
var inBrowser = typeof window !== 'undefined' && 'document' in window && 'plugins' in window.document;
var inFirefox = typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Firefox') >= 0;
// declare var print;
// declare var console;
// declare var performance;
// declare var XMLHttpRequest;
// declare var document;
// declare var getComputedStyle;
/** @define {boolean} */ var release = false;
/** @define {boolean} */ var profile = false;
export function dumpLine(line) {
    if (!release && typeof dump !== 'undefined') {
        dump(line + '\n');
    }
}
if (!jsGlobal.performance) {
    jsGlobal.performance = {};
}
if (!jsGlobal.performance.now) {
    jsGlobal.performance.now = function () {
        return Date.now();
    };
}
var START_TIME = performance.now();
/**
 * The buffer length required to contain any unsigned 32-bit integer.
 */
/** @const */ export var UINT32_CHAR_BUFFER_LENGTH = 10; // "4294967295".length;
/** @const */ export var UINT32_MAX = 0xFFFFFFFF;
/** @const */ export var UINT32_MAX_DIV_10 = 0x19999999; // UINT32_MAX / 10;
/** @const */ export var UINT32_MAX_MOD_10 = 0x5; // UINT32_MAX % 10
export function isString(value) {
    return typeof value === 'string';
}
export function isFunction(value) {
    return typeof value === 'function';
}
export function isNumber(value) {
    return typeof value === 'number';
}
export function isInteger(value) {
    return (value | 0) === value;
}
export function isArray(value) {
    return value instanceof Array;
}
export function isNumberOrString(value) {
    return typeof value === 'number' || typeof value === 'string';
}
export function isObject(value) {
    return typeof value === 'object' || typeof value === 'function';
}
export function toNumber(x) {
    return +x;
}
export function isNumericString(value) {
    // ECMAScript 5.1 - 9.8.1 Note 1, this expression is true for all
    // numbers x other than -0.
    return String(Number(value)) === value;
}
/**
 * Whether the specified |value| is a number or the string representation of a number.
 */
export function isNumeric(value) {
    if (typeof value === 'number') {
        return true;
    }
    if (typeof value === 'string') {
        // |value| is rarely numeric (it's usually an identifier), and the
        // isIndex()/isNumericString() pair is slow and expensive, so we do a
        // quick check for obvious non-numericalness first. Just checking if the
        // first char is a 7-bit identifier char catches most cases.
        var c = value.charCodeAt(0);
        if ((65 <= c && c <= 90) || // 'A'..'Z'
            (97 <= c && c <= 122) || // 'a'..'z'
            (c === 36) || // '$'
            (c === 95)) { // '_'
            return false;
        }
        return isIndex(value) || isNumericString(value);
    }
    return false;
}
/**
 * Whether the specified |value| is an unsigned 32 bit number expressed as a number
 * or string.
 */
export function isIndex(value) {
    // js/src/vm/String.cpp JSFlatString::isIndexSlow
    // http://dxr.mozilla.org/mozilla-central/source/js/src/vm/String.cpp#474
    var index = 0;
    if (typeof value === 'number') {
        index = (value | 0);
        if (value === index && index >= 0) {
            return true;
        }
        return value >>> 0 === value;
    }
    if (typeof value !== 'string') {
        return false;
    }
    var length = value.length;
    if (length === 0) {
        return false;
    }
    if (value === '0') {
        return true;
    }
    // Is there any way this will fit?
    if (length > UINT32_CHAR_BUFFER_LENGTH) {
        return false;
    }
    var i = 0;
    index = value.charCodeAt(i++) - 48 /* _0 */;
    if (index < 1 || index > 9) {
        return false;
    }
    var oldIndex = 0;
    var c = 0;
    while (i < length) {
        c = value.charCodeAt(i++) - 48 /* _0 */;
        if (c < 0 || c > 9) {
            return false;
        }
        oldIndex = index;
        index = 10 * index + c;
    }
    /*
     * Look out for "4294967296" and larger-number strings that fit in UINT32_CHAR_BUFFER_LENGTH.
     * Only unsigned 32-bit integers shall pass.
     */
    if ((oldIndex < UINT32_MAX_DIV_10) || (oldIndex === UINT32_MAX_DIV_10 && c <= UINT32_MAX_MOD_10)) {
        return true;
    }
    return false;
}
export function isNullOrUndefined(value) {
    return value == undefined;
}
export function argumentsToString(args) {
    var resultList = [];
    for (var i = 0; i < args.length; i++) {
        var arg = args[i];
        try {
            var argStr;
            if (typeof arg !== 'object' || !arg) {
                argStr = arg + '';
            }
            else if ('toString' in arg) {
                argStr = arg.toString();
            }
            else {
                argStr = Object.prototype.toString.call(arg);
            }
            resultList.push(argStr);
        }
        catch (e) {
            resultList.push('<unprintable value>');
        }
    }
    return resultList.join(', ');
}
export function getTicks() {
    return performance.now();
}
/**
 * Marsaglia's algorithm, adapted from V8. Use this if you want a deterministic random number.
 */
var Random = /** @class */ (function () {
    function Random() {
    }
    Random.seed = function (seed) {
        Random._state[0] = seed;
        Random._state[1] = seed;
    };
    Random.reset = function () {
        Random._state[0] = 0xDEAD;
        Random._state[1] = 0xBEEF;
    };
    Random.next = function () {
        var s = this._state;
        var r0 = (Math.imul(18273, s[0] & 0xFFFF) + (s[0] >>> 16)) | 0;
        s[0] = r0;
        var r1 = (Math.imul(36969, s[1] & 0xFFFF) + (s[1] >>> 16)) | 0;
        s[1] = r1;
        var x = ((r0 << 16) + (r1 & 0xFFFF)) | 0;
        // Division by 0x100000000 through multiplication by reciprocal.
        return (x < 0 ? (x + 0x100000000) : x) * 2.3283064365386962890625e-10;
    };
    Random._state = new Uint32Array([0xDEAD, 0xBEEF]);
    return Random;
}());
export { Random };
/*Math.random = function random(): number {
    return Random.next();
};*/
/**
 * This should only be called if you need fake time.
 */
export function installTimeWarper() {
    var RealDate = Date;
    // Go back in time.
    var fakeTime = 1428107694580; // 3-Apr-2015
    // Overload
    jsGlobal.Date = function (yearOrTimevalue, month, date, hour, minute, second, millisecond) {
        switch (arguments.length) {
            case 0: return new RealDate(fakeTime);
            case 1: return new RealDate(yearOrTimevalue);
            case 2: return new RealDate(yearOrTimevalue, month);
            case 3: return new RealDate(yearOrTimevalue, month, date);
            case 4: return new RealDate(yearOrTimevalue, month, date, hour);
            case 5: return new RealDate(yearOrTimevalue, month, date, hour, minute);
            case 6: return new RealDate(yearOrTimevalue, month, date, hour, minute, second);
            default: return new RealDate(yearOrTimevalue, month, date, hour, minute, second, millisecond);
        }
    };
    // Make date now deterministic.
    jsGlobal.Date.now = function () {
        return fakeTime += 10; // Advance time.
    };
    jsGlobal.Date.UTC = function () {
        return RealDate.UTC.apply(RealDate, arguments);
    };
}
function polyfillWeakMap() {
    if (typeof jsGlobal.WeakMap === 'function') {
        return; // weak map is supported
    }
    var id = 0;
    function WeakMap() {
        this.id = '$weakmap' + (id++);
    }
    WeakMap.prototype = {
        has: function (obj) {
            return obj.hasOwnProperty(this.id);
        },
        get: function (obj, defaultValue) {
            return obj.hasOwnProperty(this.id) ? obj[this.id] : defaultValue;
        },
        set: function (obj, value) {
            Object.defineProperty(obj, this.id, {
                value: value,
                enumerable: false,
                configurable: true
            });
        },
        delete: function (obj) {
            delete obj[this.id];
        }
    };
    jsGlobal.WeakMap = WeakMap;
}
polyfillWeakMap();
var useReferenceCounting = true;
var WeakList = /** @class */ (function () {
    function WeakList() {
        if (typeof ShumwayCom !== 'undefined' && ShumwayCom.getWeakMapKeys) {
            this._map = new WeakMap();
            this._id = 0;
            this._newAdditions = [];
        }
        else {
            this._list = [];
        }
    }
    WeakList.prototype.clear = function () {
        if (this._map) {
            this._map.clear();
        }
        else {
            this._list.length = 0;
        }
    };
    WeakList.prototype.push = function (value) {
        if (this._map) {
            release || assert(!this._map.has(value));
            // We store an increasing id as the value so that keys can be sorted by it.
            this._map.set(value, this._id++);
            this._newAdditions.forEach(function (additions) {
                additions.push(value);
            });
        }
        else {
            release || assert(this._list.indexOf(value) === -1);
            this._list.push(value);
        }
    };
    WeakList.prototype.remove = function (value) {
        if (this._map) {
            release || assert(this._map.has(value));
            this._map.delete(value);
        }
        else {
            release || assert(this._list.indexOf(value) > -1);
            this._list[this._list.indexOf(value)] = null;
            release || assert(this._list.indexOf(value) === -1);
        }
    };
    WeakList.prototype.forEach = function (callback) {
        if (this._map) {
            var newAdditionsToKeys = [];
            this._newAdditions.push(newAdditionsToKeys);
            var map_1 = this._map;
            var keys = ShumwayCom.getWeakMapKeys(map_1);
            // The keys returned by ShumwayCom.getWeakMapKeys are not guaranteed to
            // be in insertion order. Therefore we have to sort them manually.
            keys.sort(function (a, b) {
                return map_1.get(a) - map_1.get(b);
            });
            keys.forEach(function (value) {
                if (value._referenceCount !== 0) {
                    callback(value);
                }
            });
            // ShumwayCom.getWeakMapKeys take snapshot of the keys, but we are also
            // interested in new added keys while keys.forEach was run.
            newAdditionsToKeys.forEach(function (value) {
                if (value._referenceCount !== 0) {
                    callback(value);
                }
            });
            this._newAdditions.splice(this._newAdditions.indexOf(newAdditionsToKeys), 1);
            return;
        }
        var list = this._list;
        var zeroCount = 0;
        for (var i = 0; i < list.length; i++) {
            var value = list[i];
            if (!value) {
                continue;
            }
            if (useReferenceCounting && value._referenceCount === 0) {
                list[i] = null;
                zeroCount++;
            }
            else {
                callback(value);
            }
        }
        if (zeroCount > 16 && zeroCount > (list.length >> 2)) {
            var newList = [];
            for (var i = 0; i < list.length; i++) {
                var value = list[i];
                if (value && value._referenceCount > 0) {
                    newList.push(value);
                }
            }
            this._list = newList;
        }
    };
    Object.defineProperty(WeakList.prototype, "length", {
        get: function () {
            if (this._map) {
                // TODO: Implement this.
                return -1;
            }
            else {
                return this._list.length;
            }
        },
        enumerable: true,
        configurable: true
    });
    return WeakList;
}());
export { WeakList };
var IndentingWriter = /** @class */ (function () {
    function IndentingWriter(suppressOutput, out) {
        if (suppressOutput === void 0) { suppressOutput = false; }
        this._tab = '  ';
        this._padding = '';
        this._suppressOutput = suppressOutput;
        this._out = out || IndentingWriter._consoleOut;
        this._outNoNewline = out || IndentingWriter._consoleOutNoNewline;
    }
    Object.defineProperty(IndentingWriter.prototype, "suppressOutput", {
        get: function () {
            return this._suppressOutput;
        },
        set: function (val) {
            this._suppressOutput = val;
        },
        enumerable: true,
        configurable: true
    });
    IndentingWriter.prototype.write = function (str, writePadding) {
        if (str === void 0) { str = ''; }
        if (writePadding === void 0) { writePadding = false; }
        if (!this._suppressOutput) {
            this._outNoNewline((writePadding ? this._padding : '') + str);
        }
    };
    IndentingWriter.prototype.writeLn = function (str) {
        if (str === void 0) { str = ''; }
        if (!this._suppressOutput) {
            this._out(this._padding + str);
        }
    };
    IndentingWriter.prototype.writeObject = function (str, object) {
        if (str === void 0) { str = ''; }
        if (!this._suppressOutput) {
            this._out(this._padding + str, object);
        }
    };
    IndentingWriter.prototype.writeTimeLn = function (str) {
        if (str === void 0) { str = ''; }
        if (!this._suppressOutput) {
            this._out(this._padding + performance.now().toFixed(2) + ' ' + str);
        }
    };
    IndentingWriter.prototype.writeComment = function (str) {
        var lines = (str || '').split('\n');
        if (lines.length === 1) {
            this.writeLn('// ' + lines[0]);
        }
        else {
            this.writeLn('/**');
            for (var i = 0; i < lines.length; i++) {
                this.writeLn(' * ' + lines[i]);
            }
            this.writeLn(' */');
        }
    };
    IndentingWriter.prototype.writeLns = function (str) {
        var lines = (str || '').split('\n');
        for (var i = 0; i < lines.length; i++) {
            this.writeLn(lines[i]);
        }
    };
    IndentingWriter.prototype.errorLn = function (str) {
        if (IndentingWriter.logLevel & 1 /* Error */) {
            this.boldRedLn(str);
        }
    };
    IndentingWriter.prototype.warnLn = function (str) {
        if (IndentingWriter.logLevel & 2 /* Warn */) {
            this.yellowLn(str);
        }
    };
    IndentingWriter.prototype.debugLn = function (str) {
        if (IndentingWriter.logLevel & 4 /* Debug */) {
            this.purpleLn(str);
        }
    };
    IndentingWriter.prototype.logLn = function (str) {
        if (IndentingWriter.logLevel & 8 /* Log */) {
            this.writeLn(str);
        }
    };
    IndentingWriter.prototype.infoLn = function (str) {
        if (IndentingWriter.logLevel & 16 /* Info */) {
            this.writeLn(str);
        }
    };
    IndentingWriter.prototype.yellowLn = function (str) {
        this.colorLn(IndentingWriter.YELLOW, str);
    };
    IndentingWriter.prototype.greenLn = function (str) {
        this.colorLn(IndentingWriter.GREEN, str);
    };
    IndentingWriter.prototype.boldRedLn = function (str) {
        this.colorLn(IndentingWriter.BOLD_RED, str);
    };
    IndentingWriter.prototype.redLn = function (str) {
        this.colorLn(IndentingWriter.RED, str);
    };
    IndentingWriter.prototype.purpleLn = function (str) {
        this.colorLn(IndentingWriter.PURPLE, str);
    };
    IndentingWriter.prototype.colorLn = function (color, str) {
        if (!this._suppressOutput) {
            if (!inBrowser) {
                this._out(this._padding + color + str + IndentingWriter.ENDC);
            }
            else {
                this._out(this._padding + str);
            }
        }
    };
    IndentingWriter.prototype.redLns = function (str) {
        this.colorLns(IndentingWriter.RED, str);
    };
    IndentingWriter.prototype.colorLns = function (color, str) {
        var lines = (str || '').split('\n');
        for (var i = 0; i < lines.length; i++) {
            this.colorLn(color, lines[i]);
        }
    };
    IndentingWriter.prototype.enter = function (str) {
        if (!this._suppressOutput) {
            this._out(this._padding + str);
        }
        this.indent();
    };
    IndentingWriter.prototype.leaveAndEnter = function (str) {
        this.leave(str);
        this.indent();
    };
    IndentingWriter.prototype.leave = function (str) {
        this.outdent();
        if (!this._suppressOutput && str) {
            this._out(this._padding + str);
        }
    };
    IndentingWriter.prototype.indent = function () {
        this._padding += this._tab;
    };
    IndentingWriter.prototype.outdent = function () {
        if (this._padding.length > 0) {
            this._padding = this._padding.substring(0, this._padding.length - this._tab.length);
        }
    };
    IndentingWriter.prototype.writeArray = function (arr, detailed, noNumbers) {
        if (detailed === void 0) { detailed = false; }
        if (noNumbers === void 0) { noNumbers = false; }
        detailed = detailed || false;
        for (var i = 0, j = arr.length; i < j; i++) {
            var prefix = '';
            if (detailed) {
                if (arr[i] === null) {
                    prefix = 'null';
                }
                else if (arr[i] === undefined) {
                    prefix = 'undefined';
                }
                else {
                    prefix = arr[i].constructor.name;
                }
                prefix += ' ';
            }
            var number = noNumbers ? '' : ('' + i).padRight(' ', 4);
            this.writeLn(number + prefix + arr[i]);
        }
    };
    IndentingWriter.PURPLE = '33[94m'; //\033[94m';
    IndentingWriter.YELLOW = '33[93m'; //\033[93m';
    IndentingWriter.GREEN = '33[92m'; //\033[92m';
    IndentingWriter.RED = '33[91m'; //\033[91m';
    IndentingWriter.BOLD_RED = '33[1;91m'; //\033[1;91m';
    IndentingWriter.ENDC = '33[0m'; //\033[0m';
    IndentingWriter.logLevel = 31 /* All */;
    IndentingWriter._consoleOut = console.log.bind(console);
    IndentingWriter._consoleOutNoNewline = console.log.bind(console);
    return IndentingWriter;
}());
export { IndentingWriter };
var CircularBuffer = /** @class */ (function () {
    function CircularBuffer(Type, sizeInBits) {
        if (sizeInBits === void 0) { sizeInBits = 12; }
        this.index = 0;
        this.start = 0;
        this._size = 1 << sizeInBits;
        this._mask = this._size - 1;
        this.array = new Type(this._size);
    }
    CircularBuffer.prototype.get = function (i) {
        return this.array[i];
    };
    CircularBuffer.prototype.forEachInReverse = function (visitor) {
        if (this.isEmpty()) {
            return;
        }
        var i = this.index === 0 ? this._size - 1 : this.index - 1;
        var end = (this.start - 1) & this._mask;
        while (i !== end) {
            if (visitor(this.array[i], i)) {
                break;
            }
            i = i === 0 ? this._size - 1 : i - 1;
        }
    };
    CircularBuffer.prototype.write = function (value) {
        this.array[this.index] = value;
        this.index = (this.index + 1) & this._mask;
        if (this.index === this.start) {
            this.start = (this.start + 1) & this._mask;
        }
    };
    CircularBuffer.prototype.isFull = function () {
        return ((this.index + 1) & this._mask) === this.start;
    };
    CircularBuffer.prototype.isEmpty = function () {
        return this.index === this.start;
    };
    CircularBuffer.prototype.reset = function () {
        this.index = 0;
        this.start = 0;
    };
    return CircularBuffer;
}());
export { CircularBuffer };
var ColorStyle = /** @class */ (function () {
    function ColorStyle() {
    }
    ColorStyle.randomStyle = function () {
        if (!ColorStyle._randomStyleCache) {
            ColorStyle._randomStyleCache = [
                '#ff5e3a',
                '#ff9500',
                '#ffdb4c',
                '#87fc70',
                '#52edc7',
                '#1ad6fd',
                '#c644fc',
                '#ef4db6',
                '#4a4a4a',
                '#dbddde',
                '#ff3b30',
                '#ff9500',
                '#ffcc00',
                '#4cd964',
                '#34aadc',
                '#007aff',
                '#5856d6',
                '#ff2d55',
                '#8e8e93',
                '#c7c7cc',
                '#5ad427',
                '#c86edf',
                '#d1eefc',
                '#e0f8d8',
                '#fb2b69',
                '#f7f7f7',
                '#1d77ef',
                '#d6cec3',
                '#55efcb',
                '#ff4981',
                '#ffd3e0',
                '#f7f7f7',
                '#ff1300',
                '#1f1f21',
                '#bdbec2',
                '#ff3a2d'
            ];
        }
        return ColorStyle._randomStyleCache[(ColorStyle._nextStyle++) % ColorStyle._randomStyleCache.length];
    };
    ColorStyle.gradientColor = function (value) {
        return ColorStyle._gradient[ColorStyle._gradient.length * clamp(value, 0, 1) | 0];
    };
    ColorStyle.contrastStyle = function (rgb) {
        // http://www.w3.org/TR/AERT#color-contrast
        var c = parseInt(rgb.substr(1), 16);
        var yiq = (((c >> 16) * 299) + (((c >> 8) & 0xff) * 587) + ((c & 0xff) * 114)) / 1000;
        return (yiq >= 128) ? '#000000' : '#ffffff';
    };
    ColorStyle.reset = function () {
        ColorStyle._nextStyle = 0;
    };
    ColorStyle.TabToolbar = '#252c33';
    ColorStyle.Toolbars = '#343c45';
    ColorStyle.HighlightBlue = '#1d4f73';
    ColorStyle.LightText = '#f5f7fa';
    ColorStyle.ForegroundText = '#b6babf';
    ColorStyle.Black = '#000000';
    ColorStyle.VeryDark = '#14171a';
    ColorStyle.Dark = '#181d20';
    ColorStyle.Light = '#a9bacb';
    ColorStyle.Grey = '#8fa1b2';
    ColorStyle.DarkGrey = '#5f7387';
    ColorStyle.Blue = '#46afe3';
    ColorStyle.Purple = '#6b7abb';
    ColorStyle.Pink = '#df80ff';
    ColorStyle.Red = '#eb5368';
    ColorStyle.Orange = '#d96629';
    ColorStyle.LightOrange = '#d99b28';
    ColorStyle.Green = '#70bf53';
    ColorStyle.BlueGrey = '#5e88b0';
    ColorStyle._nextStyle = 0;
    ColorStyle._gradient = [
        '#FF0000',
        '#FF1100',
        '#FF2300',
        '#FF3400',
        '#FF4600',
        '#FF5700',
        '#FF6900',
        '#FF7B00',
        '#FF8C00',
        '#FF9E00',
        '#FFAF00',
        '#FFC100',
        '#FFD300',
        '#FFE400',
        '#FFF600',
        '#F7FF00',
        '#E5FF00',
        '#D4FF00',
        '#C2FF00',
        '#B0FF00',
        '#9FFF00',
        '#8DFF00',
        '#7CFF00',
        '#6AFF00',
        '#58FF00',
        '#47FF00',
        '#35FF00',
        '#24FF00',
        '#12FF00',
        '#00FF00' // Green
    ];
    return ColorStyle;
}());
export { ColorStyle };
/**
 * Faster release version of bounds.
 */
var Bounds = /** @class */ (function () {
    function Bounds(xMin, yMin, xMax, yMax) {
        this.xMin = xMin | 0;
        this.yMin = yMin | 0;
        this.xMax = xMax | 0;
        this.yMax = yMax | 0;
    }
    Bounds.FromUntyped = function (source) {
        return new Bounds(source.xMin, source.yMin, source.xMax, source.yMax);
    };
    Bounds.FromRectangle = function (source) {
        return new Bounds(source.x * 20 | 0, source.y * 20 | 0, (source.x + source.width) * 20 | 0, (source.y + source.height) * 20 | 0);
    };
    Bounds.prototype.setElements = function (xMin, yMin, xMax, yMax) {
        this.xMin = xMin;
        this.yMin = yMin;
        this.xMax = xMax;
        this.yMax = yMax;
    };
    Bounds.prototype.copyFrom = function (source) {
        this.setElements(source.xMin, source.yMin, source.xMax, source.yMax);
    };
    Bounds.prototype.contains = function (x, y) {
        return x < this.xMin !== x < this.xMax &&
            y < this.yMin !== y < this.yMax;
    };
    Bounds.prototype.unionInPlace = function (other) {
        if (other.isEmpty()) {
            return;
        }
        this.extendByPoint(other.xMin, other.yMin);
        this.extendByPoint(other.xMax, other.yMax);
    };
    Bounds.prototype.extendByPoint = function (x, y) {
        this.extendByX(x);
        this.extendByY(y);
    };
    Bounds.prototype.extendByX = function (x) {
        // Exclude default values.
        if (this.xMin === 0x8000000) {
            this.xMin = this.xMax = x;
            return;
        }
        this.xMin = Math.min(this.xMin, x);
        this.xMax = Math.max(this.xMax, x);
    };
    Bounds.prototype.extendByY = function (y) {
        // Exclude default values.
        if (this.yMin === 0x8000000) {
            this.yMin = this.yMax = y;
            return;
        }
        this.yMin = Math.min(this.yMin, y);
        this.yMax = Math.max(this.yMax, y);
    };
    Bounds.prototype.intersects = function (toIntersect) {
        return this.contains(toIntersect.xMin, toIntersect.yMin) ||
            this.contains(toIntersect.xMax, toIntersect.yMax);
    };
    Bounds.prototype.isEmpty = function () {
        return this.xMax <= this.xMin || this.yMax <= this.yMin;
    };
    Object.defineProperty(Bounds.prototype, "width", {
        get: function () {
            return this.xMax - this.xMin;
        },
        set: function (value) {
            this.xMax = this.xMin + value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Bounds.prototype, "height", {
        get: function () {
            return this.yMax - this.yMin;
        },
        set: function (value) {
            this.yMax = this.yMin + value;
        },
        enumerable: true,
        configurable: true
    });
    Bounds.prototype.getBaseWidth = function (angle) {
        var u = Math.abs(Math.cos(angle));
        var v = Math.abs(Math.sin(angle));
        return u * (this.xMax - this.xMin) + v * (this.yMax - this.yMin);
    };
    Bounds.prototype.getBaseHeight = function (angle) {
        var u = Math.abs(Math.cos(angle));
        var v = Math.abs(Math.sin(angle));
        return v * (this.xMax - this.xMin) + u * (this.yMax - this.yMin);
    };
    Bounds.prototype.setEmpty = function () {
        this.xMin = this.yMin = this.xMax = this.yMax = 0;
    };
    /**
     * Set all fields to the sentinel value 0x8000000.
     *
     * This is what Flash uses to indicate uninitialized bounds. Important for bounds calculation
     * in `Graphics` instances, which start out with empty bounds but must not just extend them
     * from an 0,0 origin.
     */
    Bounds.prototype.setToSentinels = function () {
        this.xMin = this.yMin = this.xMax = this.yMax = 0x8000000;
    };
    Bounds.prototype.clone = function () {
        return new Bounds(this.xMin, this.yMin, this.xMax, this.yMax);
    };
    Bounds.prototype.toString = function () {
        return '{ ' +
            'xMin: ' + this.xMin + ', ' +
            'xMin: ' + this.yMin + ', ' +
            'xMax: ' + this.xMax + ', ' +
            'xMax: ' + this.yMax +
            ' }';
    };
    return Bounds;
}());
export { Bounds };
/**
 * Slower debug version of bounds, makes sure that all points have integer coordinates.
 */
var DebugBounds = /** @class */ (function () {
    function DebugBounds(xMin, yMin, xMax, yMax) {
        assert(isInteger(xMin));
        assert(isInteger(yMin));
        assert(isInteger(xMax));
        assert(isInteger(yMax));
        this._xMin = xMin | 0;
        this._yMin = yMin | 0;
        this._xMax = xMax | 0;
        this._yMax = yMax | 0;
        this.assertValid();
    }
    DebugBounds.FromUntyped = function (source) {
        return new DebugBounds(source.xMin, source.yMin, source.xMax, source.yMax);
    };
    DebugBounds.FromRectangle = function (source) {
        return new DebugBounds(source.x * 20 | 0, source.y * 20 | 0, (source.x + source.width) * 20 | 0, (source.y + source.height) * 20 | 0);
    };
    DebugBounds.prototype.setElements = function (xMin, yMin, xMax, yMax) {
        this.xMin = xMin;
        this.yMin = yMin;
        this.xMax = xMax;
        this.yMax = yMax;
    };
    DebugBounds.prototype.copyFrom = function (source) {
        this.setElements(source.xMin, source.yMin, source.xMax, source.yMax);
    };
    DebugBounds.prototype.contains = function (x, y) {
        return x < this.xMin !== x < this.xMax &&
            y < this.yMin !== y < this.yMax;
    };
    DebugBounds.prototype.unionInPlace = function (other) {
        if (other.isEmpty()) {
            return;
        }
        this.extendByPoint(other.xMin, other.yMin);
        this.extendByPoint(other.xMax, other.yMax);
    };
    DebugBounds.prototype.extendByPoint = function (x, y) {
        this.extendByX(x);
        this.extendByY(y);
    };
    DebugBounds.prototype.extendByX = function (x) {
        if (this.xMin === 0x8000000) {
            this.xMin = this.xMax = x;
            return;
        }
        this.xMin = Math.min(this.xMin, x);
        this.xMax = Math.max(this.xMax, x);
    };
    DebugBounds.prototype.extendByY = function (y) {
        if (this.yMin === 0x8000000) {
            this.yMin = this.yMax = y;
            return;
        }
        this.yMin = Math.min(this.yMin, y);
        this.yMax = Math.max(this.yMax, y);
    };
    DebugBounds.prototype.intersects = function (toIntersect) {
        return this.contains(toIntersect._xMin, toIntersect._yMin) ||
            this.contains(toIntersect._xMax, toIntersect._yMax);
    };
    DebugBounds.prototype.isEmpty = function () {
        return this._xMax <= this._xMin || this._yMax <= this._yMin;
    };
    Object.defineProperty(DebugBounds.prototype, "xMin", {
        get: function () {
            return this._xMin;
        },
        set: function (value) {
            assert(isInteger(value));
            this._xMin = value;
            this.assertValid();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DebugBounds.prototype, "yMin", {
        get: function () {
            return this._yMin;
        },
        set: function (value) {
            assert(isInteger(value));
            this._yMin = value | 0;
            this.assertValid();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DebugBounds.prototype, "xMax", {
        get: function () {
            return this._xMax;
        },
        set: function (value) {
            assert(isInteger(value));
            this._xMax = value | 0;
            this.assertValid();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DebugBounds.prototype, "width", {
        get: function () {
            return this._xMax - this._xMin;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DebugBounds.prototype, "yMax", {
        get: function () {
            return this._yMax;
        },
        set: function (value) {
            assert(isInteger(value));
            this._yMax = value | 0;
            this.assertValid();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DebugBounds.prototype, "height", {
        get: function () {
            return this._yMax - this._yMin;
        },
        enumerable: true,
        configurable: true
    });
    DebugBounds.prototype.getBaseWidth = function (angle) {
        var u = Math.abs(Math.cos(angle));
        var v = Math.abs(Math.sin(angle));
        return u * (this._xMax - this._xMin) + v * (this._yMax - this._yMin);
    };
    DebugBounds.prototype.getBaseHeight = function (angle) {
        var u = Math.abs(Math.cos(angle));
        var v = Math.abs(Math.sin(angle));
        return v * (this._xMax - this._xMin) + u * (this._yMax - this._yMin);
    };
    DebugBounds.prototype.setEmpty = function () {
        this._xMin = this._yMin = this._xMax = this._yMax = 0;
    };
    DebugBounds.prototype.clone = function () {
        return new DebugBounds(this.xMin, this.yMin, this.xMax, this.yMax);
    };
    DebugBounds.prototype.toString = function () {
        return '{ ' +
            'xMin: ' + this._xMin + ', ' +
            'yMin: ' + this._yMin + ', ' +
            'xMax: ' + this._xMax + ', ' +
            'yMax: ' + this._yMax +
            ' }';
    };
    DebugBounds.prototype.assertValid = function () {
        //      release || assert(this._xMax >= this._xMin);
        //      release || assert(this._yMax >= this._yMin);
    };
    return DebugBounds;
}());
export { DebugBounds };
/**
 * Override Bounds with a slower by safer version, don't do this in release mode.
 */
// Shumway.Bounds = DebugBounds;
var Color = /** @class */ (function () {
    function Color(r, g, b, a) {
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;
    }
    Color.FromARGB = function (argb) {
        return new Color((argb >> 16 & 0xFF) / 255, (argb >> 8 & 0xFF) / 255, (argb >> 0 & 0xFF) / 255, (argb >> 24 & 0xFF) / 255);
    };
    Color.FromRGBA = function (rgba) {
        return Color.FromARGB(RGBAToARGB(rgba));
    };
    Color.prototype.toRGBA = function () {
        return (this.r * 255) << 24 | (this.g * 255) << 16 | (this.b * 255) << 8 | (this.a * 255);
    };
    Color.prototype.toCSSStyle = function () {
        return rgbaToCSSStyle(this.toRGBA());
    };
    Color.prototype.set = function (other) {
        this.r = other.r;
        this.g = other.g;
        this.b = other.b;
        this.a = other.a;
    };
    Color.randomColor = function (alpha) {
        if (alpha === void 0) { alpha = 1; }
        return new Color(Math.random(), Math.random(), Math.random(), alpha);
    };
    Color.parseColor = function (color) {
        if (!Color.colorCache) {
            Color.colorCache = Object.create(null);
        }
        if (Color.colorCache[color]) {
            return Color.colorCache[color];
        }
        // TODO: Obviously slow, but it will do for now.
        var span = document.createElement('span');
        document.body.appendChild(span);
        span.style.backgroundColor = color;
        var rgb = getComputedStyle(span).backgroundColor;
        document.body.removeChild(span);
        var m = /^rgb\((\d+), (\d+), (\d+)\)$/.exec(rgb);
        if (!m)
            m = /^rgba\((\d+), (\d+), (\d+), ([\d.]+)\)$/.exec(rgb);
        var result = new Color(0, 0, 0, 0);
        result.r = parseFloat(m[1]) / 255;
        result.g = parseFloat(m[2]) / 255;
        result.b = parseFloat(m[3]) / 255;
        result.a = m[4] ? parseFloat(m[4]) / 255 : 1;
        return Color.colorCache[color] = result;
    };
    Color.Red = new Color(1, 0, 0, 1);
    Color.Green = new Color(0, 1, 0, 1);
    Color.Blue = new Color(0, 0, 1, 1);
    Color.None = new Color(0, 0, 0, 0);
    Color.White = new Color(1, 1, 1, 1);
    Color.Black = new Color(0, 0, 0, 1);
    Color.colorCache = {};
    return Color;
}());
export { Color };
export function registerCSSFont(id, data, forceFontInit) {
    if (!inBrowser) {
        Debug.warning('Cannot register CSS font outside the browser');
        return;
    }
    var head = document.head;
    head.insertBefore(document.createElement('style'), head.firstChild);
    var style = document.styleSheets[0];
    var rule = '@font-face{font-family:swffont' + id + ';src:url(data:font/opentype;base64,' +
        base64EncodeBytes(data) + ')' + '}';
    style.insertRule(rule, style.cssRules.length);
    // In at least Chrome, the browser only decodes a font once it's used in the page at all.
    // Because it still does so asynchronously, we create a with some text using the font, take
    // some measurement from it (which will turn out wrong because the font isn't yet available),
    // and then remove the node again. Then, magic happens. After a bit of time for said magic to
    // take hold, the font is available for actual use on canvas.
    // TODO: remove the need for magic by implementing this in terms of the font loading API.
    if (forceFontInit) {
        var node = document.createElement('div');
        node.style.fontFamily = 'swffont' + id;
        node.innerHTML = 'hello';
        document.body.appendChild(node);
        var dummyHeight = node.clientHeight;
        document.body.removeChild(node);
    }
}
var Callback = /** @class */ (function () {
    function Callback() {
        this._queues = {};
    }
    Callback.prototype.register = function (type, callback) {
        assert(type);
        assert(callback);
        var queue = this._queues[type];
        if (queue) {
            if (queue.indexOf(callback) > -1) {
                return;
            }
        }
        else {
            queue = this._queues[type] = [];
        }
        queue.push(callback);
    };
    Callback.prototype.unregister = function (type, callback) {
        assert(type);
        assert(callback);
        var queue = this._queues[type];
        if (!queue) {
            return;
        }
        var i = queue.indexOf(callback);
        if (i !== -1) {
            queue.splice(i, 1);
        }
        if (queue.length === 0) {
            this._queues[type] = null;
        }
    };
    Callback.prototype.notify = function (type, args) {
        var queue = this._queues[type];
        if (!queue) {
            return;
        }
        queue = queue.slice();
        var args = Array.prototype.slice.call(arguments, 0);
        for (var i = 0; i < queue.length; i++) {
            var callback = queue[i];
            callback.apply(null, args);
        }
    };
    Callback.prototype.notify1 = function (type, value) {
        var queue = this._queues[type];
        if (!queue) {
            return;
        }
        queue = queue.slice();
        for (var i = 0; i < queue.length; i++) {
            var callback = queue[i];
            callback(type, value);
        }
    };
    return Callback;
}());
export { Callback };
export var ImageType;
(function (ImageType) {
    ImageType[ImageType["None"] = 0] = "None";
    /**
     * Premultiplied ARGB (byte-order).
     */
    ImageType[ImageType["PremultipliedAlphaARGB"] = 1] = "PremultipliedAlphaARGB";
    /**
     * Unpremultiplied ARGB (byte-order).
     */
    ImageType[ImageType["StraightAlphaARGB"] = 2] = "StraightAlphaARGB";
    /**
     * Unpremultiplied RGBA (byte-order), this is what putImageData expects.
     */
    ImageType[ImageType["StraightAlphaRGBA"] = 3] = "StraightAlphaRGBA";
    ImageType[ImageType["JPEG"] = 4] = "JPEG";
    ImageType[ImageType["PNG"] = 5] = "PNG";
    ImageType[ImageType["GIF"] = 6] = "GIF";
})(ImageType || (ImageType = {}));
export function getMIMETypeForImageType(type) {
    switch (type) {
        case ImageType.JPEG: return 'image/jpeg';
        case ImageType.PNG: return 'image/png';
        case ImageType.GIF: return 'image/gif';
        default: return 'text/plain';
    }
}
var PromiseWrapper = /** @class */ (function () {
    function PromiseWrapper() {
        this.promise = new Promise(function (resolve, reject) {
            this.resolve = resolve;
            this.reject = reject;
        }.bind(this));
    }
    PromiseWrapper.prototype.then = function (onFulfilled, onRejected) {
        return this.promise.then(onFulfilled, onRejected);
    };
    return PromiseWrapper;
}());
export { PromiseWrapper };
if (typeof exports !== 'undefined') {
    exports['Shumway'] = Shumway;
}
/**
 * Extend builtin prototypes.
 *
 * TODO: Go through the code and remove all references to these.
 */
(function () {
    function extendBuiltin(prototype, property, value) {
        if (!prototype[property]) {
            Object.defineProperty(prototype, property, { value: value,
                writable: true,
                configurable: true,
                enumerable: false });
        }
    }
    function removeColors(s) {
        return s.replace(/\033\[[0-9]*m/g, '');
    }
    extendBuiltin(String.prototype, 'padRight', function (c, n) {
        var str = this;
        var length = removeColors(str).length;
        if (!c || length >= n) {
            return str;
        }
        var max = (n - length) / c.length;
        for (var i = 0; i < max; i++) {
            str += c;
        }
        return str;
    });
    extendBuiltin(String.prototype, 'padLeft', function (c, n) {
        var str = this;
        var length = str.length;
        if (!c || length >= n) {
            return str;
        }
        var max = (n - length) / c.length;
        for (var i = 0; i < max; i++) {
            str = c + str;
        }
        return str;
    });
    extendBuiltin(String.prototype, 'trim', function () {
        return this.replace(/^\s+|\s+$/g, '');
    });
    extendBuiltin(String.prototype, 'endsWith', function (str) {
        return this.indexOf(str, this.length - str.length) !== -1;
    });
    extendBuiltin(Array.prototype, 'replace', function (x, y) {
        if (x === y) {
            return 0;
        }
        var count = 0;
        for (var i = 0; i < this.length; i++) {
            if (this[i] === x) {
                this[i] = y;
                count++;
            }
        }
        return count;
    });
})();
